import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Pie, Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { FaUsers, FaBriefcase, FaGraduationCap, FaChartPie, FaTrophy, FaMapMarkerAlt, FaArrowRight, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { getAlmamater } from '../utils/education';
import { supabase } from '../lib/supabase';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import usePageTitle from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Comprehensive city/province coordinates
const CITY_COORDS = {
  // ============ PROVINSI (nama provinsi) ============
  'aceh': [5.5483, 95.3238], 'sumatera utara': [3.5952, 98.6722], 'sumut': [3.5952, 98.6722],
  'sumatera barat': [-0.9471, 100.4172], 'sumbar': [-0.9471, 100.4172],
  'riau': [0.5071, 101.4478], 'kepulauan riau': [0.9187, 104.4471], 'kepri': [0.9187, 104.4471],
  'jambi': [-1.6101, 103.6131], 'sumatera selatan': [-2.9761, 104.7754], 'sumsel': [-2.9761, 104.7754],
  'bengkulu': [-3.7928, 102.2608], 'lampung': [-5.4500, 105.2667],
  'bangka belitung': [-2.1290, 106.1169], 'babel': [-2.1290, 106.1169],
  'banten': [-6.1103, 106.1640], 'dki jakarta': [-6.2088, 106.8456],
  'jawa barat': [-6.9175, 107.6191], 'jabar': [-6.9175, 107.6191],
  'jawa tengah': [-6.9666, 110.4196], 'jateng': [-6.9666, 110.4196],
  'diy': [-7.7956, 110.3695], 'di yogyakarta': [-7.7956, 110.3695],
  'jawa timur': [-7.2575, 112.7521], 'jatim': [-7.2575, 112.7521],
  'bali': [-8.3405, 115.0920], 'nusa tenggara barat': [-8.5833, 116.1167], 'ntb': [-8.5833, 116.1167],
  'nusa tenggara timur': [-10.1772, 123.6070], 'ntt': [-10.1772, 123.6070],
  'kalimantan barat': [-0.0263, 109.3425], 'kalbar': [-0.0263, 109.3425],
  'kalimantan tengah': [-1.6815, 113.3824], 'kalteng': [-1.6815, 113.3824],
  'kalimantan selatan': [-3.3186, 114.5944], 'kalsel': [-3.3186, 114.5944],
  'kalimantan timur': [-0.4948, 117.1436], 'kaltim': [-0.4948, 117.1436],
  'kalimantan utara': [3.0738, 116.0414], 'kaltara': [3.0738, 116.0414],
  'sulawesi utara': [1.4748, 124.8421], 'sulut': [1.4748, 124.8421],
  'sulawesi tengah': [-0.8917, 119.8707], 'sulteng': [-0.8917, 119.8707],
  'sulawesi selatan': [-5.1477, 119.4327], 'sulsel': [-5.1477, 119.4327],
  'sulawesi tenggara': [-3.9985, 122.5130], 'sultra': [-3.9985, 122.5130],
  'sulawesi barat': [-2.6748, 118.8885], 'sulbar': [-2.6748, 118.8885],
  'gorontalo': [0.5435, 123.0568],
  'maluku': [-3.6954, 128.1814], 'maluku utara': [0.7736, 127.3611], 'malut': [0.7736, 127.3611],
  'papua': [-2.5916, 140.6690], 'papua barat': [-0.8615, 134.0620],
  'papua selatan': [-6.1218, 137.0155], 'papua tengah': [-3.7000, 137.5000],
  'papua pegunungan': [-4.0833, 138.0833], 'papua barat daya': [-1.3932, 132.1890],
  'iku': [-3.0000, 116.5000],

  // ============ SUMATERA ============
  // Aceh
  'banda aceh': [5.5483, 95.3238], 'lhokseumawe': [5.1801, 97.1507], 'langsa': [4.4686, 97.9681],
  'sabang': [5.8933, 95.3191], 'subulussalam': [2.6419, 98.0000], 'meulaboh': [4.1370, 96.1289],
  'takengon': [4.6277, 96.8463], 'sigli': [5.3835, 96.0087], 'bireuen': [5.2043, 96.6949],
  'blangkejeren': [3.9547, 97.3711], 'kutacane': [3.4978, 97.8130],

  // Sumatera Utara
  'medan': [3.5952, 98.6722], 'binjai': [3.6001, 98.4854], 'pematang siantar': [2.9545, 99.0489],
  'tanjung balai': [2.9583, 99.8000], 'tebing tinggi': [3.3253, 99.1627],
  'padang sidempuan': [1.3795, 99.2717], 'sibolga': [1.7427, 98.7792],
  'gunungsitoli': [1.2890, 97.6147], 'rantau prapat': [2.1000, 99.8333],
  'kisaran': [2.9841, 99.6157], 'lubuk pakam': [3.5582, 98.8567],
  'kabanjahe': [3.0992, 98.4911], 'balige': [2.3342, 99.0650],
  'porsea': [2.4667, 99.0714], 'tarutung': [2.0167, 98.9667],
  'sidikalang': [2.7361, 98.3194], 'pangururan': [2.6239, 98.7278],
  'parapat': [2.6644, 98.9383], 'deli serdang': [3.4167, 98.6833],

  // Sumatera Barat
  'padang': [-0.9471, 100.4172], 'bukittinggi': [-0.3065, 100.3691], 'payakumbuh': [-0.2199, 100.6308],
  'solok': [-0.7989, 100.6544], 'sawahlunto': [-0.6832, 100.7764], 'padang panjang': [-0.4669, 100.3986],
  'pariaman': [-0.6237, 100.1196], 'batusangkar': [-0.4581, 100.5936], 'lubuk basung': [-0.3053, 100.0839],
  'painan': [-1.3500, 100.5833], 'muaro sijunjung': [-0.6656, 100.9464],

  // Riau
  'pekanbaru': [0.5071, 101.4478], 'dumai': [1.6667, 101.4500], 'bengkalis': [1.4833, 102.0833],
  'siak': [1.1050, 102.1464], 'tembilahan': [-0.3244, 103.1594], 'pangkalan kerinci': [0.3639, 101.8531],
  'selat panjang': [1.0167, 102.7167], 'bagan siapi-api': [2.1667, 100.8167],
  'rengat': [-0.3500, 102.5500], 'pasir pengaraian': [0.8500, 100.1167],
  'taluk kuantan': [-0.5333, 101.5500], 'bangkinang': [0.3167, 101.0500],

  // Kepulauan Riau
  'tanjung pinang': [0.9187, 104.4471], 'batam': [1.0456, 104.0305], 'karimun': [1.0333, 103.3833],
  'tanjung balai karimun': [1.0333, 103.3833], 'natuna': [3.9278, 108.3889],
  'lingga': [-0.2167, 104.5833], 'anambas': [3.2333, 106.2500],

  // Jambi
  'kota jambi': [-1.6101, 103.6131], 'muara bungo': [-1.4981, 102.1064], 'sungai penuh': [-2.0589, 101.3925],
  'muara bulian': [-1.6833, 103.2833], 'kuala tungkal': [-0.8167, 103.4667],
  'muara tebo': [-1.4583, 102.3833], 'bangko': [-2.0667, 102.9167],
  'sarolangun': [-2.3072, 102.8847], 'sungai bahar': [-1.5833, 103.6000],

  // Sumatera Selatan
  'palembang': [-2.9761, 104.7754], 'lubuk linggau': [-3.2917, 102.8611], 'pagar alam': [-4.0223, 103.2515],
  'prabumulih': [-3.4344, 104.2297], 'baturaja': [-4.1319, 104.1647], 'kayu agung': [-3.3833, 104.8167],
  'lahat': [-3.8015, 103.5406], 'muara enim': [-3.6833, 104.1000], 'sekayu': [-2.8833, 104.0000],
  'indralaya': [-3.2394, 104.6467], 'martapura': [-4.2753, 104.3214],

  // Bengkulu
  'kota bengkulu': [-3.7928, 102.2608], 'curup': [-3.4750, 102.5250], 'manna': [-4.4500, 102.9167],
  'mukomuko': [-2.5833, 101.1000], 'arga makmur': [-3.3830, 102.2170], 'kepahiang': [-3.6167, 102.5667],

  // Lampung
  'bandar lampung': [-5.4500, 105.2667], 'metro': [-5.1143, 105.3069], 'kotabumi': [-4.8333, 104.9000],
  'kalianda': [-5.7167, 105.6167], 'liwa': [-5.0500, 104.0667], 'pringsewu': [-5.3583, 104.9833],
  'tulang bawang': [-4.3500, 105.7500], 'way kanan': [-4.5667, 104.4333],
  'tanggamus': [-5.4333, 104.6167], 'pesawaran': [-5.5167, 105.0833],

  // Bangka Belitung
  'pangkal pinang': [-2.1290, 106.1169], 'sungailiat': [-1.8589, 106.1186], 'manggar': [-2.8833, 108.2667],
  'tanjung pandan': [-2.7500, 107.6167], 'mentok': [-2.0667, 105.1667], 'toboali': [-2.9167, 106.4500],
  'belinyu': [-1.6167, 106.0333],

  // ============ JAWA ============
  // DKI Jakarta
  'jakarta': [-6.2088, 106.8456], 'jakarta pusat': [-6.1862, 106.8340], 'jakarta selatan': [-6.2615, 106.8106],
  'jakarta barat': [-6.1673, 106.7588], 'jakarta utara': [-6.1384, 106.8638], 'jakarta timur': [-6.2250, 106.9004],

  // Banten
  'serang': [-6.1103, 106.1640], 'cilegon': [-6.0024, 106.0511], 'tangerang': [-6.1781, 106.6319],
  'tangerang selatan': [-6.3388, 106.6869], 'tangsel': [-6.3388, 106.6869],
  'pandeglang': [-6.3105, 106.1028], 'rangkasbitung': [-6.3500, 106.2500],
  'lebak': [-6.5667, 106.1000], 'tigaraksa': [-6.2833, 106.4833],

  // Jawa Barat
  'bandung': [-6.9175, 107.6191], 'bogor': [-6.5971, 106.8060], 'depok': [-6.4025, 106.7942],
  'bekasi': [-6.2383, 106.9756], 'cirebon': [-6.7320, 108.5523], 'tasikmalaya': [-7.3506, 108.2172],
  'sukabumi': [-6.9277, 106.9300], 'cimahi': [-6.8841, 107.5413], 'banjar': [-7.3700, 108.5400],
  'garut': [-7.2275, 107.9089], 'kuningan': [-6.9756, 108.4836], 'majalengka': [-6.8333, 108.2333],
  'sumedang': [-6.8533, 107.9186], 'subang': [-6.5681, 107.7525], 'purwakarta': [-6.5569, 107.4453],
  'karawang': [-6.3089, 107.2972], 'indramayu': [-6.3278, 108.3222], 'cianjur': [-6.8225, 107.1394],
  'pangandaran': [-7.6928, 108.6572], 'ciamis': [-7.3297, 108.3508], 'bandung barat': [-6.8414, 107.4689],

  // Jawa Tengah
  'semarang': [-6.9666, 110.4196], 'solo': [-7.5755, 110.8243], 'surakarta': [-7.5755, 110.8243],
  'purwokerto': [-7.4244, 109.2378], 'tegal': [-6.8797, 109.1426], 'pekalongan': [-6.8886, 109.6753],
  'magelang': [-7.4797, 110.2177], 'salatiga': [-7.3305, 110.5084], 'klaten': [-7.7056, 110.6042],
  'kudus': [-6.8048, 110.8405], 'boyolali': [-7.5317, 110.5950], 'demak': [-6.8936, 110.6381],
  'kendal': [-6.9167, 110.2000], 'brebes': [-6.8717, 109.0417], 'pemalang': [-6.8917, 109.3833],
  'jepara': [-6.5933, 110.6778], 'blora': [-6.9667, 111.4167], 'rembang': [-6.7083, 111.3500],
  'pati': [-6.7500, 111.0333], 'wonosobo': [-7.3636, 109.9019], 'temanggung': [-7.3167, 110.1667],
  'purbalingga': [-7.3886, 109.3631], 'banjarnegara': [-7.3833, 109.6833], 'kebumen': [-7.6697, 109.6511],
  'cilacap': [-7.7267, 109.0092], 'sragen': [-7.4333, 111.0167], 'karanganyar': [-7.5972, 110.9583],
  'wonogiri': [-7.8167, 110.9167], 'sukoharjo': [-7.6833, 110.8333], 'batang': [-6.8958, 109.7250],
  'bumiayu': [-7.2333, 109.0167], 'purworejo': [-7.7167, 110.0083],

  // DI Yogyakarta
  'yogyakarta': [-7.7956, 110.3695], 'sleman': [-7.7167, 110.3500], 'bantul': [-7.8833, 110.3333],
  'gunung kidul': [-7.9833, 110.6167], 'gunungkidul': [-7.9833, 110.6167],
  'kulon progo': [-7.8333, 110.1667], 'kulonprogo': [-7.8333, 110.1667],

  // Jawa Timur
  'surabaya': [-7.2575, 112.7521], 'malang': [-7.9666, 112.6326], 'batu': [-7.8831, 112.5254],
  'kediri': [-7.8480, 112.0180], 'madiun': [-7.6298, 111.5230], 'jember': [-8.1845, 113.6681],
  'sidoarjo': [-7.4478, 112.7183], 'gresik': [-7.1548, 112.6530],
  'blitar': [-8.0955, 112.1610], 'mojokerto': [-7.4704, 112.4401], 'pasuruan': [-7.6469, 112.9075],
  'probolinggo': [-7.7543, 113.2159], 'lumajang': [-8.1333, 113.2333], 'bondowoso': [-7.9167, 113.8167],
  'situbondo': [-7.7065, 114.0090], 'banyuwangi': [-8.2192, 114.3691], 'tuban': [-6.8986, 112.0500],
  'lamongan': [-7.1194, 112.4175], 'bojonegoro': [-7.1500, 111.8833], 'ngawi': [-7.4042, 111.4500],
  'magetan': [-7.6500, 111.3333], 'ponorogo': [-7.8653, 111.4594], 'trenggalek': [-8.0500, 111.7000],
  'tulungagung': [-8.0653, 111.9000], 'nganjuk': [-7.6000, 111.9000], 'pacitan': [-8.2000, 111.1000],
  'bangkalan': [-7.0456, 112.7350], 'sampang': [-7.1833, 113.2333], 'pamekasan': [-7.1579, 113.4744],
  'sumenep': [-7.0167, 113.8667], 'mojosari': [-7.5333, 112.5167], 'kepanjen': [-8.1500, 112.5667],

  // ============ KALIMANTAN ============
  'pontianak': [-0.0263, 109.3425], 'singkawang': [0.9089, 108.9872], 'ketapang': [-1.8167, 109.9667],
  'sintang': [0.0833, 111.4833], 'putussibau': [0.8667, 112.9333], 'sambas': [1.3500, 109.3000],
  'sanggau': [0.1333, 110.5833], 'mempawah': [0.0833, 109.0167],
  'palangkaraya': [-2.2136, 113.9108], 'palangka raya': [-2.2136, 113.9108],
  'sampit': [-2.5333, 112.9500], 'pangkalan bun': [-2.6833, 111.6167],
  'muara teweh': [-0.9500, 114.9000], 'kuala kapuas': [-3.0500, 114.3833],
  'banjarmasin': [-3.3186, 114.5944], 'banjarbaru': [-3.4416, 114.8413],
  'martapura kalsel': [-3.4167, 114.8500], 'batulicin': [-3.4500, 115.9833],
  'kotabaru': [-3.2833, 116.1667], 'rantau': [-2.3167, 115.2333],
  'barabai': [-2.5833, 115.3833], 'amuntai': [-2.4167, 115.2500],
  'samarinda': [-0.4948, 117.1436], 'balikpapan': [-1.2379, 116.8529],
  'bontang': [0.1236, 117.4741], 'tenggarong': [-0.4167, 116.9833],
  'sangatta': [0.5000, 117.5000], 'berau': [2.1500, 117.4833],
  'tanjung selor': [2.8500, 117.3667], 'tarakan': [3.2967, 117.5786],
  'nunukan': [4.1333, 117.6667], 'malinau': [3.5667, 116.6333],
  'tanjung redeb': [2.1500, 117.4833],

  // ============ SULAWESI ============
  'manado': [1.4748, 124.8421], 'bitung': [1.4403, 125.1217], 'tomohon': [1.3228, 124.8377],
  'kotamobagu': [0.7333, 124.3167], 'tahuna': [3.5917, 125.5000],
  'gorontalo kota': [0.5435, 123.0568], 'limboto': [0.6167, 122.9833],
  'palu kota': [-0.8917, 119.8707], 'luwuk': [-0.9500, 122.7833], 'poso': [-1.3833, 120.7500],
  'toli-toli': [1.0500, 120.7833], 'donggala': [-0.6833, 119.7333],
  'makassar': [-5.1477, 119.4327], 'pare-pare': [-4.0135, 119.6253], 'palopo': [-2.9928, 120.1969],
  'maros': [-4.9900, 119.5700], 'pangkep': [-4.8167, 119.5500], 'barru': [-4.4167, 119.6000],
  'bone': [-4.5369, 120.3256], 'wajo': [-4.0500, 120.2000], 'soppeng': [-4.3500, 120.0000],
  'sinjai': [-5.1333, 120.2500], 'bulukumba': [-5.4500, 120.2000], 'bantaeng': [-5.5333, 119.9500],
  'jeneponto': [-5.6500, 119.7500], 'takalar': [-5.4167, 119.4833], 'gowa': [-5.3167, 119.4833],
  'toraja': [-3.0500, 119.8667], 'tana toraja': [-3.0500, 119.8667], 'rantepao': [-2.9711, 119.8983],
  'enrekang': [-3.5667, 119.7833], 'pinrang': [-3.7833, 119.6500],
  'kendari kota': [-3.9985, 122.5130], 'bau-bau': [-5.4712, 122.6153], 'baubau': [-5.4712, 122.6153],
  'kolaka': [-4.0500, 121.5833], 'raha': [-4.9833, 122.7167],
  'mamuju kota': [-2.6748, 118.8885], 'majene': [-3.5333, 118.9667], 'polewali': [-3.4500, 119.3333],

  // ============ BALI & NUSA TENGGARA ============
  'denpasar': [-8.6500, 115.2167], 'singaraja': [-8.1120, 115.0882], 'gianyar': [-8.5333, 115.3167],
  'tabanan': [-8.5417, 115.1250], 'klungkung': [-8.5333, 115.4000], 'bangli': [-8.4500, 115.3500],
  'karangasem': [-8.4500, 115.6000], 'negara': [-8.3500, 114.6167], 'ubud': [-8.5069, 115.2625],
  'kuta': [-8.7220, 115.1695], 'nusa dua': [-8.8000, 115.2333],
  'lombok': [-8.5833, 116.1167], 'mataram': [-8.5833, 116.1167], 'praya': [-8.7167, 116.2833],
  'selong': [-8.6500, 116.5500], 'sumbawa besar': [-8.4833, 117.4167], 'bima': [-8.4606, 118.7267],
  'dompu': [-8.5333, 118.4500], 'raba': [-8.4606, 118.7267],
  'kupang': [-10.1772, 123.6070], 'soe': [-9.8667, 124.2833], 'kefamenanu': [-9.4500, 124.4833],
  'atambua': [-9.1000, 124.9000], 'maumere': [-8.6167, 122.2167], 'ende': [-8.8500, 121.6667],
  'ruteng': [-8.6167, 120.4667], 'labuan bajo': [-8.4917, 119.8889], 'bajawa': [-8.7833, 121.0833],
  'kalabahi': [-8.2167, 124.5167], 'waingapu': [-9.6500, 120.2500], 'waikabubak': [-9.6333, 119.4167],
  'larantuka': [-8.3500, 122.9833], 'lewoleba': [-8.3667, 123.5833],
  'flores': [-8.6500, 121.5000], 'sumba': [-9.6500, 120.2500], 'timor': [-9.8333, 124.0333],
  'alor': [-8.2167, 124.5167], 'manggarai': [-8.6167, 120.4667],

  // ============ MALUKU ============
  'ambon': [-3.6954, 128.1814], 'tual': [-5.6333, 132.7333], 'masohi': [-3.3500, 128.9333],
  'namlea': [-3.2333, 127.1000], 'dobo': [-5.7667, 134.2167], 'saumlaki': [-7.9833, 131.3000],
  'ternate': [0.7736, 127.3611], 'tidore': [0.6833, 127.4000], 'sofifi': [0.7333, 127.5667],
  'tobelo': [1.7167, 128.0000], 'labuha': [-0.6333, 127.5000], 'sanana': [-2.0500, 125.9833],
  'jailolo': [1.0667, 127.4167],

  // ============ PAPUA ============
  'jayapura': [-2.5916, 140.6690], 'sorong': [-0.8613, 131.2550], 'manokwari': [-0.8615, 134.0620],
  'merauke': [-8.4932, 140.4018], 'timika': [-4.5500, 136.8833], 'nabire': [-3.3667, 135.5000],
  'biak': [-1.1833, 136.0833], 'fakfak': [-2.9333, 132.3000], 'kaimana': [-3.6500, 133.7500],
  'wamena': [-4.0833, 138.9333], 'sentani': [-2.5833, 140.5167], 'abepura': [-2.5916, 140.6690],
  'sarmi': [-1.8667, 138.7500], 'serui': [-1.8833, 136.2333],
  'raja ampat': [-0.4833, 130.6667], 'teluk bintuni': [-2.1167, 133.5167],
  'teluk wondama': [-2.8333, 134.5000],

  // ============ KOTA-KOTA INDONESIA LAINNYA ============
  'tanjung balai asahan': [2.9583, 99.8000],
  'padang sidimpuan': [1.3795, 99.2717],
  'cikarang': [-6.2567, 107.1467], 'serpong': [-6.3200, 106.6700],
  'cibinong': [-6.4793, 106.8520], 'cibubur': [-6.3687, 106.8766],
  'pondok gede': [-6.2805, 106.9170], 'sawangan': [-6.4196, 106.7701],
  'cinere': [-6.3375, 106.7943], 'pamulang': [-6.3421, 106.7263],
  'ciputat': [-6.3175, 106.7464], 'bsd': [-6.3017, 106.6529],
  'kelapa gading': [-6.1583, 106.9050], 'kemang': [-6.2611, 106.8119],
  'menteng': [-6.1960, 106.8375], 'senayan': [-6.2271, 106.8023],
  'pluit': [-6.1258, 106.7911], 'sunter': [-6.1467, 106.8933],
  'cengkareng': [-6.1467, 106.7250],

  // ============ LUAR NEGERI ============
  // Asia Tenggara
  'singapore': [1.3521, 103.8198], 'singapura': [1.3521, 103.8198],
  'kuala lumpur': [3.1390, 101.6869], 'malaysia': [3.1390, 101.6869],
  'bangkok': [13.7563, 100.5018], 'thailand': [13.7563, 100.5018],
  'manila': [14.5995, 120.9842], 'filipina': [14.5995, 120.9842],
  'ho chi minh': [10.8231, 106.6297], 'vietnam': [21.0278, 105.8342], 'hanoi': [21.0278, 105.8342],
  'phnom penh': [11.5564, 104.9282], 'kamboja': [11.5564, 104.9282],
  'vientiane': [17.9757, 102.6331], 'laos': [17.9757, 102.6331],
  'yangon': [16.8661, 96.1951], 'myanmar': [16.8661, 96.1951],
  'brunei': [4.9431, 114.9425], 'bandar seri begawan': [4.9431, 114.9425],
  'timor leste': [-8.5569, 125.5603], 'dili': [-8.5569, 125.5603],

  // Asia Timur
  'tokyo': [35.6762, 139.6503], 'jepang': [35.6762, 139.6503], 'japan': [35.6762, 139.6503],
  'osaka': [34.6937, 135.5023], 'kyoto': [35.0116, 135.7681], 'nagoya': [35.1815, 136.9066],
  'fukuoka': [33.5902, 130.4017], 'sapporo': [43.0618, 141.3545], 'sendai': [38.2682, 140.8694],
  'yokohama': [35.4437, 139.6380], 'kobe': [34.6901, 135.1956], 'hiroshima': [34.3853, 132.4553],
  'seoul': [37.5665, 126.9780], 'korea': [37.5665, 126.9780], 'korea selatan': [37.5665, 126.9780],
  'busan': [35.1796, 129.0756], 'daejeon': [36.3504, 127.3845], 'daegu': [35.8714, 128.6014],
  'beijing': [39.9042, 116.4074], 'shanghai': [31.2304, 121.4737], 'guangzhou': [23.1291, 113.2644],
  'shenzhen': [22.5431, 114.0579], 'china': [39.9042, 116.4074], 'tiongkok': [39.9042, 116.4074],
  'hong kong': [22.3193, 114.1694], 'hongkong': [22.3193, 114.1694],
  'taipei': [25.0330, 121.5654], 'taiwan': [25.0330, 121.5654],
  'mongolia': [47.8864, 106.9057], 'ulaanbaatar': [47.8864, 106.9057],

  // Asia Selatan & Tengah
  'india': [28.6139, 77.2090], 'new delhi': [28.6139, 77.2090], 'mumbai': [19.0760, 72.8777],
  'bangalore': [12.9716, 77.5946], 'chennai': [13.0827, 80.2707], 'kolkata': [22.5726, 88.3639],
  'pakistan': [33.6844, 73.0479], 'islamabad': [33.6844, 73.0479],
  'bangladesh': [23.8103, 90.4125], 'dhaka': [23.8103, 90.4125],
  'sri lanka': [6.9271, 79.8612], 'colombo': [6.9271, 79.8612],
  'nepal': [27.7172, 85.3240], 'kathmandu': [27.7172, 85.3240],

  // Asia Barat / Timur Tengah
  'arab saudi': [24.7136, 46.6753], 'saudi arabia': [24.7136, 46.6753], 'riyadh': [24.7136, 46.6753],
  'jeddah': [21.4858, 39.1925], 'mekkah': [21.3891, 39.8579], 'madinah': [24.4539, 39.6142],
  'dubai': [25.2048, 55.2708], 'uae': [25.2048, 55.2708], 'uni emirat arab': [25.2048, 55.2708],
  'qatar': [25.2854, 51.5310], 'doha': [25.2854, 51.5310],
  'turki': [41.0082, 28.9784], 'turkey': [41.0082, 28.9784], 'istanbul': [41.0082, 28.9784],
  'ankara': [39.9334, 32.8597],

  // Eropa
  'london': [51.5074, -0.1278], 'inggris': [51.5074, -0.1278], 'uk': [51.5074, -0.1278],
  'england': [51.5074, -0.1278], 'manchester': [53.4808, -2.2426], 'edinburgh': [55.9533, -3.1883],
  'berlin': [52.5200, 13.4050], 'jerman': [52.5200, 13.4050], 'germany': [52.5200, 13.4050],
  'munich': [48.1351, 11.5820], 'hamburg': [53.5511, 9.9937], 'frankfurt': [50.1109, 8.6821],
  'belanda': [52.3676, 4.9041], 'amsterdam': [52.3676, 4.9041], 'netherlands': [52.3676, 4.9041],
  'rotterdam': [51.9244, 4.4777], 'den haag': [52.0705, 4.3007], 'delft': [52.0116, 4.3571],
  'eindhoven': [51.4416, 5.4697], 'leiden': [52.1601, 4.4970], 'groningen': [53.2194, 6.5665],
  'paris': [48.8566, 2.3522], 'prancis': [48.8566, 2.3522], 'france': [48.8566, 2.3522],
  'lyon': [45.7640, 4.8357], 'marseille': [43.2965, 5.3698],
  'roma': [41.9028, 12.4964], 'italia': [41.9028, 12.4964], 'italy': [41.9028, 12.4964], 'milan': [45.4642, 9.1900],
  'madrid': [40.4168, -3.7038], 'spanyol': [40.4168, -3.7038], 'spain': [40.4168, -3.7038], 'barcelona': [41.3874, 2.1686],
  'lisbon': [38.7223, -9.1393], 'portugal': [38.7223, -9.1393],
  'vienna': [48.2082, 16.3738], 'austria': [48.2082, 16.3738],
  'zurich': [47.3769, 8.5417], 'swiss': [46.9480, 7.4474], 'switzerland': [46.9480, 7.4474], 'bern': [46.9480, 7.4474],
  'brussels': [50.8503, 4.3517], 'belgia': [50.8503, 4.3517], 'belgium': [50.8503, 4.3517],
  'stockholm': [59.3293, 18.0686], 'swedia': [59.3293, 18.0686], 'sweden': [59.3293, 18.0686],
  'oslo': [59.9139, 10.7522], 'norwegia': [59.9139, 10.7522], 'norway': [59.9139, 10.7522],
  'helsinki': [60.1699, 24.9384], 'finlandia': [60.1699, 24.9384], 'finland': [60.1699, 24.9384],
  'copenhagen': [55.6761, 12.5683], 'denmark': [55.6761, 12.5683], 'danmark': [55.6761, 12.5683],
  'dublin': [53.3498, -6.2603], 'irlandia': [53.3498, -6.2603], 'ireland': [53.3498, -6.2603],
  'praha': [50.0755, 14.4378], 'czech': [50.0755, 14.4378], 'polandia': [52.2297, 21.0122], 'warsaw': [52.2297, 21.0122],
  'budapest': [47.4979, 19.0402], 'hungaria': [47.4979, 19.0402],
  'moscow': [55.7558, 37.6173], 'rusia': [55.7558, 37.6173], 'russia': [55.7558, 37.6173],

  // Amerika
  'amerika': [40.7128, -74.0060], 'usa': [40.7128, -74.0060], 'united states': [40.7128, -74.0060],
  'new york': [40.7128, -74.0060], 'los angeles': [34.0522, -118.2437], 'chicago': [41.8781, -87.6298],
  'san francisco': [37.7749, -122.4194], 'seattle': [47.6062, -122.3321], 'boston': [42.3601, -71.0589],
  'washington dc': [38.9072, -77.0369], 'houston': [29.7604, -95.3698], 'dallas': [32.7767, -96.7970],
  'atlanta': [33.7490, -84.3880], 'miami': [25.7617, -80.1918], 'denver': [39.7392, -104.9903],
  'san diego': [32.7157, -117.1611], 'austin': [30.2672, -97.7431], 'portland': [45.5152, -122.6784],
  'philadelphia': [39.9526, -75.1652], 'pittsburgh': [40.4406, -79.9959],
  'canada': [43.6532, -79.3832], 'toronto': [43.6532, -79.3832], 'vancouver': [49.2827, -123.1207],
  'montreal': [45.5017, -73.5673], 'ottawa': [45.4215, -75.6972],
  'brazil': [-23.5505, -46.6333], 'sao paulo': [-23.5505, -46.6333],
  'mexico city': [19.4326, -99.1332], 'mexico': [19.4326, -99.1332],
  'buenos aires': [-34.6037, -58.3816], 'argentina': [-34.6037, -58.3816],

  // Australia & Oceania
  'melbourne': [-37.8136, 144.9631], 'sydney': [-33.8688, 151.2093],
  'australia': [-33.8688, 151.2093], 'brisbane': [-27.4698, 153.0251],
  'perth': [-31.9505, 115.8605], 'adelaide': [-34.9285, 138.6007],
  'canberra': [-35.2809, 149.1300], 'gold coast': [-28.0167, 153.4000],
  'new zealand': [-36.8485, 174.7633], 'selandia baru': [-36.8485, 174.7633],
  'auckland': [-36.8485, 174.7633], 'wellington': [-41.2865, 174.7762],

  // Afrika
  'cairo': [30.0444, 31.2357], 'mesir': [30.0444, 31.2357], 'egypt': [30.0444, 31.2357],
  'south africa': [-33.9249, 18.4241], 'afrika selatan': [-33.9249, 18.4241],
  'cape town': [-33.9249, 18.4241], 'nairobi': [-1.2921, 36.8219], 'kenya': [-1.2921, 36.8219],
  'lagos': [6.5244, 3.3792], 'nigeria': [6.5244, 3.3792],
  'morocco': [33.9716, -6.8498], 'maroko': [33.9716, -6.8498], 'rabat': [33.9716, -6.8498],
  'tunisia': [36.8065, 10.1815], 'tunis': [36.8065, 10.1815],
};

const PALETTES = {
  job: ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#64748b'],
  edu: ['#3b82f6', '#a855f7', '#22c55e', '#eab308', '#e11d48', '#0891b2', '#d946ef', '#84cc16', '#f43f5e', '#6366f1'],
};

const Statistics = () => {
  usePageTitle();
  const { dark } = useTheme();
  const { t } = useLanguage();
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [showAllEdu, setShowAllEdu] = useState(false);

  useEffect(() => {
    supabase.from('alumni').select('*').order('id', { ascending: false })
      .then(({ data }) => setAlumni(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Case-insensitive counting: normalize to lowercase key, keep first-seen casing as display label
  const countBy = (arr, key) => {
    const map = {};
    const labelMap = {};
    arr.forEach(a => {
      const v = a[key];
      if (v) {
        const normalized = v.trim().toLowerCase();
        if (!labelMap[normalized]) labelMap[normalized] = v.trim();
        map[normalized] = (map[normalized] || 0) + 1;
      }
    });
    const result = {};
    Object.keys(map).forEach(k => { result[labelMap[k]] = map[k]; });
    return result;
  };

  const countByAlmamater = (arr) => {
    const map = {};
    const labelMap = {};
    arr.forEach(a => {
      const alm = getAlmamater(a.education);
      if (alm) {
        const normalized = alm.trim().toLowerCase();
        if (!labelMap[normalized]) labelMap[normalized] = alm.trim();
        map[normalized] = (map[normalized] || 0) + 1;
      }
    });
    const result = {};
    Object.keys(map).forEach(k => { result[labelMap[k]] = map[k]; });
    return result;
  };

  const countByResearch = (arr) => {
    const map = {};
    const labelMap = {};
    arr.forEach(a => {
      if (a.research_interests) {
        a.research_interests.split(',').forEach(ri => {
          const t = ri.trim();
          if (t) {
            const normalized = t.toLowerCase();
            if (!labelMap[normalized]) labelMap[normalized] = t;
            map[normalized] = (map[normalized] || 0) + 1;
          }
        });
      }
    });
    const result = {};
    Object.keys(map).forEach(k => { result[labelMap[k]] = map[k]; });
    return result;
  };

  const jobStats = countBy(alumni, 'job');
  const eduStats = countByAlmamater(alumni);
  const researchStats = countByResearch(alumni);

  // Domisili stats (case-insensitive)
  const domisiliStats = {};
  const domisiliLabelMap = {};
  alumni.forEach(a => {
    if (a.domisili) {
      const d = a.domisili.trim();
      const normalized = d.toLowerCase();
      if (!domisiliLabelMap[normalized]) domisiliLabelMap[normalized] = d;
      domisiliStats[normalized] = (domisiliStats[normalized] || 0) + 1;
    }
  });
  // Remap domisili to display labels
  const domisiliDisplay = {};
  Object.keys(domisiliStats).forEach(k => { domisiliDisplay[domisiliLabelMap[k]] = domisiliStats[k]; });
  const domisiliWithCoords = Object.entries(domisiliDisplay).map(([city, count]) => {
    const key = city.toLowerCase().trim();
    const coords = CITY_COORDS[key];
    return { city, count, coords };
  }).filter(d => d.coords);
  const totalMapped = domisiliWithCoords.reduce((s, d) => s + d.count, 0);
  const totalWithDomisili = Object.values(domisiliDisplay).reduce((s, c) => s + c, 0);

  // PIMNAS year distribution
  const pimnasStats = {};
  alumni.forEach(a => {
    if (a.tahun_pimnas) pimnasStats[a.tahun_pimnas] = (pimnasStats[a.tahun_pimnas] || 0) + 1;
  });
  const pimnasLabels = Object.keys(pimnasStats).sort((a, b) => Number(a) - Number(b));
  const pimnasData = pimnasLabels.map(k => pimnasStats[k]);
  const uniquePimnasEditions = pimnasLabels.length;

  const buildChartData = (stats, palette, topN = 10) => {
    const sorted = Object.entries(stats).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, topN);
    const rest = sorted.slice(topN);
    const labels = top.map(([k]) => k);
    const data = top.map(([, v]) => v);
    const colors = palette.concat(
      Array.from({ length: Math.max(0, top.length - palette.length) }, (_, i) =>
        palette[i % palette.length]
      )
    ).slice(0, top.length);
    if (rest.length > 0) {
      const othersSum = rest.reduce((s, [, v]) => s + v, 0);
      labels.push(t('stats.others') || 'Lainnya');
      data.push(othersSum);
      colors.push('#94a3b8');
    }
    return {
      labels,
      datasets: [{ data, backgroundColor: colors, borderWidth: 0, hoverOffset: 8 }],
    };
  };

  const textColor = dark ? '#cbd5e1' : '#374151';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((s, v) => s + v, 0);
            const pct = total > 0 ? Math.round((ctx.raw / total) * 100) : 0;
            return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
          },
        },
      },
    },
  };

  const totalAlumni = alumni.length;

  // Top 5 jobs
  const topJobs = Object.entries(jobStats).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <FaChartPie className="text-gold-400" /> {t('stats.title')}
          </h1>
          <p className="text-gray-300">{t('stats.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5 mb-10">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <FaUsers className="text-primary-600 text-lg sm:text-xl" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalAlumni}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t('stats.totalAlumni')}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <FaBriefcase className="text-amber-600 text-lg sm:text-xl" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{Object.keys(jobStats).length}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t('stats.careerFields')}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <FaGraduationCap className="text-purple-600 text-lg sm:text-xl" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{Object.keys(eduStats).length}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t('stats.eduInstitutions')}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <FaTrophy className="text-gold-600 text-lg sm:text-xl" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{uniquePimnasEditions}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t('stats.pimnasEditions')}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left col-span-2 sm:col-span-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <FaMapMarkerAlt className="text-emerald-600 text-lg sm:text-xl" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{Object.keys(domisiliStats).length}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t('stats.domicileCities')}</p>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              {/* Job Distribution */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('stats.jobDistribution')}</h3>
                {Object.keys(jobStats).length > 0 ? (
                  <>
                    <div className="h-64 sm:h-72 flex items-center justify-center">
                      <Doughnut data={buildChartData(jobStats, PALETTES.job, 10)} options={chartOptions} />
                    </div>
                    {/* Custom Legend */}
                    <div className={`mt-4 space-y-1.5 ${!showAllJobs && Object.keys(jobStats).length > 10 ? 'max-h-48 overflow-y-auto' : ''}`}>
                      {(showAllJobs
                        ? Object.entries(jobStats).sort((a, b) => b[1] - a[1])
                        : Object.entries(jobStats).sort((a, b) => b[1] - a[1]).slice(0, 10)
                      ).map(([label, count], i) => {
                        const total = Object.values(jobStats).reduce((s, v) => s + v, 0);
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        const color = i < PALETTES.job.length ? PALETTES.job[i] : PALETTES.job[i % PALETTES.job.length];
                        return (
                          <div key={label} className="flex items-center gap-2 text-sm">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: showAllJobs && i >= 10 ? '#94a3b8' : color }}></span>
                            <span className="text-gray-700 truncate flex-1">{label}</span>
                            <span className="text-gray-500 flex-shrink-0">{count} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                    {Object.keys(jobStats).length > 10 && (
                      <button
                        onClick={() => setShowAllJobs(!showAllJobs)}
                        className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
                      >
                        {showAllJobs ? <>{t('stats.showLess')} <FaChevronUp className="text-xs" /></> : <>{t('stats.showAll')} ({Object.keys(jobStats).length}) <FaChevronDown className="text-xs" /></>}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400 text-center pt-16">{t('stats.noData')}</p>
                )}
              </div>
              {/* Education Distribution */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('stats.eduDistribution')}</h3>
                {Object.keys(eduStats).length > 0 ? (
                  <>
                    <div className="h-64 sm:h-72 flex items-center justify-center">
                      <Pie data={buildChartData(eduStats, PALETTES.edu, 10)} options={chartOptions} />
                    </div>
                    {/* Custom Legend */}
                    <div className={`mt-4 space-y-1.5 ${!showAllEdu && Object.keys(eduStats).length > 10 ? 'max-h-48 overflow-y-auto' : ''}`}>
                      {(showAllEdu
                        ? Object.entries(eduStats).sort((a, b) => b[1] - a[1])
                        : Object.entries(eduStats).sort((a, b) => b[1] - a[1]).slice(0, 10)
                      ).map(([label, count], i) => {
                        const total = Object.values(eduStats).reduce((s, v) => s + v, 0);
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        const color = i < PALETTES.edu.length ? PALETTES.edu[i] : PALETTES.edu[i % PALETTES.edu.length];
                        return (
                          <div key={label} className="flex items-center gap-2 text-sm">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: showAllEdu && i >= 10 ? '#94a3b8' : color }}></span>
                            <span className="text-gray-700 truncate flex-1">{label}</span>
                            <span className="text-gray-500 flex-shrink-0">{count} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                    {Object.keys(eduStats).length > 10 && (
                      <button
                        onClick={() => setShowAllEdu(!showAllEdu)}
                        className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
                      >
                        {showAllEdu ? <>{t('stats.showLess')} <FaChevronUp className="text-xs" /></> : <>{t('stats.showAll')} ({Object.keys(eduStats).length}) <FaChevronDown className="text-xs" /></>}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400 text-center pt-16">{t('stats.noData')}</p>
                )}
              </div>
            </div>

            {/* PIMNAS Year Distribution Bar Chart */}
            {pimnasLabels.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-10">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FaTrophy className="text-gold-500" /> {t('stats.pimnasDistribution')}
                </h3>
                <div className="h-72">
                  <Bar
                    data={{
                      labels: pimnasLabels.map(y => `ke-${y}`),
                      datasets: [{
                        label: t('stats.alumniCount'),
                        data: pimnasData,
                        backgroundColor: '#6366f1',
                        borderRadius: 8,
                        maxBarThickness: 40,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        y: { beginAtZero: true, ticks: { stepSize: 1, color: textColor }, grid: { color: dark ? '#334155' : '#e5e7eb' } },
                        x: { grid: { display: false }, ticks: { color: textColor } },
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Top Jobs Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('stats.top5Careers')}</h3>
                <Link to="/statistik/karir" className="text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
                  {t('stats.viewAll')} <FaArrowRight className="text-xs" />
                </Link>
              </div>
              {topJobs.length === 0 ? (
                <p className="text-gray-400">{t('stats.noData')}</p>
              ) : (
                <div className="space-y-3">
                  {topJobs.map(([job, count], i) => {
                    const pct = totalAlumni > 0 ? Math.round((count / totalAlumni) * 100) : 0;
                    return (
                      <div key={job}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{i + 1}. {job}</span>
                          <span className="text-sm text-gray-500">{count} {t('stats.alumniLabel')} ({pct}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                          <div className="bg-gradient-to-r from-primary-500 to-gold-500 h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Research Interests */}
            {Object.keys(researchStats).length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('stats.researchInterests')}</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(researchStats).sort((a, b) => b[1] - a[1]).map(([topic, count]) => (
                    <span key={topic} className="bg-primary-50 text-primary-700 text-sm font-medium px-3 py-1.5 rounded-full">
                      {topic} <span className="text-primary-400 ml-1">({count})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Peta Persebaran Alumni */}
            {totalWithDomisili > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-emerald-500" /> {t('stats.alumniMap')}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {totalMapped} {t('stats.alumniFrom')} {Object.keys(domisiliDisplay).length} {t('stats.citiesOnMap')}
                  {totalWithDomisili > totalMapped && <span className="text-gray-400"> ({totalWithDomisili - totalMapped} {t('stats.unrecognizedCities')})</span>}
                </p>
                <div className="rounded-xl overflow-hidden border border-gray-200 h-[320px] sm:h-[480px]">
                  <MapContainer
                    center={[-2.5, 118]}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url={dark
                        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
                    />
                    {domisiliWithCoords.map((d) => (
                      <CircleMarker
                        key={d.city}
                        center={d.coords}
                        radius={Math.max(8, Math.min(30, d.count * 6))}
                        pathOptions={{
                          color: '#4f46e5',
                          fillColor: '#6366f1',
                          fillOpacity: 0.6,
                          weight: 2,
                        }}
                      >
                        <LeafletTooltip direction="top" offset={[0, -8]} permanent={d.count >= 3}>
                          <span className="font-semibold">{d.city}</span>: {d.count} {t('stats.alumniLabel')}
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                </div>

                {/* City breakdown list */}
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {Object.entries(domisiliDisplay).sort((a, b) => b[1] - a[1]).map(([city, count]) => (
                    <div key={city} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <FaMapMarkerAlt className="text-emerald-500 text-xs flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">{city}</span>
                      <span className="text-xs font-semibold text-gray-500 ml-auto">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Statistics;
